import Dexie from 'dexie';
import {
  isConnectionError,
  logger,
  RETRY_DELAY_MS,
  type PersistenceFailure,
  type PersistenceFailureHandler,
} from '@damofire/data-commons';

export {
  createMemoryRecoveryQueue,
  isConnectionError,
  isQuotaExceededError,
  RETRY_DELAY_MS,
  type PersistenceFailure,
  type PersistenceFailureHandler,
  type PersistenceStatus,
} from '@damofire/data-commons';

export interface DexieDiagnoseSnapshot {
  databaseName: string;
  databaseVersion: number;
  tables: string[];
  isOpen: boolean;
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface RetryOptions {
  delayMs?: number;
  attempts?: number;
}

const attemptOnce = <T>(fn: () => Promise<T>): Promise<T> => fn();

/**
 * Single retry on connection errors only. Never retries quota errors.
 * Returns result plus attempts count so callers report 1 vs 2 accurately.
 */
export async function withConnectionRetry<T>(
  operation: string,
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<{ result: T; attempts: number }> {
  const delayMs = opts.delayMs ?? RETRY_DELAY_MS;
  const maxAttempts = opts.attempts ?? 2;
  try {
    return { result: await attemptOnce(fn), attempts: 1 };
  } catch (e) {
    if (maxAttempts <= 1 || !isConnectionError(e)) {
      throw e;
    }
    logger.warn('Dexie connection error, retrying once.', { operation, error: e, category: 'survey' });
    await delay(delayMs);
    try {
      return { result: await attemptOnce(fn), attempts: 2 };
    } catch (retryError) {
      logger.error('Dexie retry failed.', { operation, error: retryError, category: 'survey' });
      throw retryError;
    }
  }
}

/**
 * Shared Dexie base: failure hook + diagnose snapshot. Both app databases
 * extend it so the hook contract stays in one module.
 */
export class DexieDatabaseBase extends Dexie {
  private failureHandler: PersistenceFailureHandler | undefined;

  onPersistenceFailure(handler: PersistenceFailureHandler): void {
    this.failureHandler = handler;
  }

  get persistenceFailureHandler(): PersistenceFailureHandler | undefined {
    return this.failureHandler;
  }

  diagnose(): DexieDiagnoseSnapshot {
    return {
      databaseName: this.name,
      databaseVersion: this.verno,
      tables: this.tables.map((t) => t.name),
      isOpen: this.isOpen(),
    };
  }
}

export function notifyPersistenceFailure(
  handler: PersistenceFailureHandler | undefined,
  failure: PersistenceFailure,
): void {
  logger.error('Dexie persistence failed terminally, data NOT persisted.', { ...failure, category: 'survey' });
  void Promise.resolve()
    .then(() => handler?.(failure))
    .catch((handlerError: unknown) => {
      logger.error('Dexie failure handler threw, failure kept in logs only.', { error: handlerError, category: 'survey' });
    });
}
